//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React from 'react';

// Keyboard behavior for a hand-rolled menu (a Popper holding menuitem rows,
// which MUI's Menu is not managing). The header's demo-data menu is the one
// consumer today.

// Focus first enabled menu item, or menu container if none available.
export const focusFirstMenuItem = (
  menuRef: { current: HTMLElement | null }
) => {
  if (!menuRef.current) return;
  const firstItem = menuRef.current.querySelector<HTMLElement>(
    '[role="menuitem"]:not([aria-disabled="true"])'
  );
  if (firstItem) {
    firstItem.focus();
  } else {
    // If no enabled items, focus the menu container for keyboard handling.
    menuRef.current.focus();
  }
};

/**
 * Arrow keys cycle the enabled items; Escape and Tab close and hand focus
 * back to `returnFocusTo` — the element the user activated to open the menu,
 * which can be any of several launchers, so it is passed as the element
 * recorded at open time rather than a ref to one fixed button. A close never
 * aims at a node that has left the document: the menu outliving its anchor
 * (a taskbar refit, say) leaves focus where it is instead.
 */
export const handleMenuKeyDown = (
  event: React.KeyboardEvent<HTMLElement>,
  closeMenu: () => void,
  returnFocusTo: Element | null
) => {
  const menu = event.currentTarget;
  const items = Array.from(
    menu.querySelectorAll<HTMLElement>(
      '[role="menuitem"]:not([aria-disabled="true"])')
  );
  const currentIndex =
    items.findIndex(item => item === document.activeElement);

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      if (items.length === 0) return;
      if (currentIndex < items.length - 1) {
        items[currentIndex + 1].focus();
      } else {
        items[0].focus();
      }
      break;
    case 'ArrowUp':
      event.preventDefault();
      if (items.length === 0) return;
      if (currentIndex > 0) {
        items[currentIndex - 1].focus();
      } else {
        items[items.length - 1].focus();
      }
      break;
    case 'Escape':
    case 'Tab':
      event.preventDefault();
      closeMenu();
      if (returnFocusTo instanceof HTMLElement && returnFocusTo.isConnected)
        returnFocusTo.focus();
      break;
    default:
      break;
  }
};
